import { Badge, Box, Button, FocusTrap, Group, Modal, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { FeatureAnnouncement } from '../../utils/featureAnnouncements';

interface FeatureAnnouncementModalProps {
  announcement: FeatureAnnouncement;
  opened: boolean;
  onCta: () => void;
  onDismiss: () => void;
  /**
   * When set, the CTA is disabled and this explains why (e.g. the user is
   * at their meal plan limit so "Plan your week" would dead-end). Product
   * state belongs to the host; the modal just renders it.
   */
  ctaDisabledReason?: string;
}

// One-time modal for a major feature release. Layout: full-bleed photography
// strip melting into the card, then title, supporting copy, icon highlights,
// and a single primary CTA. Everything renders from the announcement entry,
// so the next big feature reuses this shell untouched.
export const FeatureAnnouncementModal = ({
  announcement,
  opened,
  onCta,
  onDismiss,
  ctaDisabledReason,
}: FeatureAnnouncementModalProps) => (
  <Modal
    opened={opened}
    onClose={onDismiss}
    centered
    size={500}
    radius="xl"
    padding={0}
    withCloseButton={false}
    overlayProps={{ backgroundOpacity: 0.45, blur: 4 }}
    transitionProps={{ transition: 'pop', duration: 250 }}
    styles={{
      content: { overflow: 'hidden' },
    }}
  >
    {/* Without this, the focus trap lands on the CTA and it opens
        pre-highlighted; tab still reaches it for keyboard users */}
    <FocusTrap.InitialFocus />
    <Box style={{ position: 'relative', height: 180 }}>
      <Group gap={2} wrap="nowrap" h="100%">
        {announcement.heroImages.map((image) => (
          <Box key={image.src} style={{ flex: 1, height: '100%', overflow: 'hidden' }}>
            <img
              src={image.src}
              alt={image.alt}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: `center ${image.focusY ?? 50}%`,
                display: 'block',
              }}
            />
          </Box>
        ))}
      </Group>
      {/* Photography melts into the card body instead of ending on a hard line */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(255,255,255,0.75) 90%, #fff 100%)',
          pointerEvents: 'none',
        }}
      />
      <Badge
        variant="filled"
        color="orange"
        size="lg"
        style={{
          position: 'absolute',
          top: 14,
          // Top-right: the left panel's food (the pancakes' pat of butter)
          // sits exactly under the top-left corner
          right: 16,
          boxShadow: '0 2px 10px rgba(0,0,0,0.18)',
        }}
      >
        {announcement.badge}
      </Badge>
    </Box>

    <Stack gap="lg" px={28} pb={28} pt={4}>
      <Stack gap={6}>
        <Title order={2} fw={700} lh={1.2}>
          {announcement.title}
        </Title>
        <Text size="sm" c="dimmed" lh={1.55}>
          {announcement.description}
        </Text>
      </Stack>

      <Stack gap="md">
        {announcement.highlights.map(({ icon: Icon, title, text }) => (
          <Group key={title} gap="md" wrap="nowrap" align="flex-start">
            <ThemeIcon size={38} radius="md" variant="light" color="orange">
              <Icon size={20} stroke={1.8} />
            </ThemeIcon>
            <Box>
              <Text size="sm" fw={600} c="dark">
                {title}
              </Text>
              <Text size="xs" c="dimmed" lh={1.5}>
                {text}
              </Text>
            </Box>
          </Group>
        ))}
      </Stack>

      <Stack gap={6}>
        <Button
          fullWidth
          size="md"
          radius="xl"
          color="orange"
          onClick={onCta}
          disabled={!!ctaDisabledReason}
          style={{ fontWeight: 600 }}
        >
          {announcement.ctaLabel}
        </Button>
        <Button fullWidth size="sm" radius="xl" variant="subtle" color="gray" onClick={onDismiss}>
          {announcement.secondaryLabel}
        </Button>
        {ctaDisabledReason && (
          <Text size="xs" c="dimmed" ta="center" lh={1.5} mt={2}>
            {ctaDisabledReason}
          </Text>
        )}
      </Stack>
    </Stack>
  </Modal>
);
